import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { Avatar } from '@/ui/primitives';
import { cn } from '@/lib/utils';
import { useAgentes } from '../api';
import { AgentesModuleTabs } from './AgentesModuleTabs';
import type { Agente } from '../schemas';

function fmtHora(ts: string | null): string {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) {
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
  }
  const ontem = new Date(now);
  ontem.setDate(now.getDate() - 1);
  if (d.toDateString() === ontem.toDateString()) return 'Ontem';
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
}

export function AgentesView() {
  const navigate = useNavigate();
  const { data: agentes, isLoading, isError } = useAgentes();
  const [filtro, setFiltro] = useState<'todas' | 'nao_lidas'>('todas');

  const totalNaoLidas = (agentes ?? []).reduce((s, a) => s + (a.nao_lidas || 0), 0);
  const lista = (agentes ?? []).filter((a) => (filtro === 'nao_lidas' ? a.nao_lidas > 0 : true));

  function abrir(a: Agente) {
    if (a.usa_canais) void navigate({ to: '/agentes/$agente', params: { agente: a.id } });
    else void navigate({ to: '/agentes/$agente/$canal', params: { agente: a.id, canal: 'direto' } });
  }

  return (
    <div className="flex h-full flex-col bg-bg">
      {/* header */}
      <header className="flex items-center justify-between px-4 pt-5 pb-2">
        <h1 className="text-[1.75rem] font-bold tracking-tight text-text">Agentes</h1>
        <button
          type="button"
          className="flex size-9 items-center justify-center rounded-full text-text-2 hover:bg-surface2"
          aria-label="Buscar"
        >
          <Search size={21} strokeWidth={1.75} />
        </button>
      </header>

      {/* abas do módulo (admin vê Autoaprendizado; some pra não-admin) */}
      <AgentesModuleTabs active="conversas" className="px-4" />

      {/* filtros */}
      <div className="flex items-center gap-2 px-4 pb-2 pt-2">
        <FiltroChip active={filtro === 'todas'} onClick={() => setFiltro('todas')}>
          Todas
        </FiltroChip>
        <FiltroChip active={filtro === 'nao_lidas'} onClick={() => setFiltro('nao_lidas')} badge={totalNaoLidas}>
          Não lidas
        </FiltroChip>
      </div>

      {/* lista */}
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading && (
          <div className="flex flex-col gap-2 p-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="h-14 rounded-xl bg-surface2 animate-pulse" />
            ))}
          </div>
        )}
        {isError && <p className="p-4 text-sm text-text-2">Não foi possível carregar os agentes.</p>}
        {!isLoading && !isError && lista.length === 0 && (
          <p className="p-6 text-center text-sm text-text-muted">
            {filtro === 'nao_lidas' ? 'Nenhuma conversa não lida.' : 'Nenhum agente disponível.'}
          </p>
        )}
        {lista.map((a) => (
          <AgenteRow key={a.id} a={a} onClick={() => abrir(a)} />
        ))}
      </div>
    </div>
  );
}

function FiltroChip({
  active,
  badge,
  onClick,
  children,
}: {
  active: boolean;
  badge?: number;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-3.5 h-8 text-[0.82rem] font-medium transition-colors',
        active ? 'bg-[var(--accent-soft)] text-accent' : 'bg-surface2 text-text-2 hover:text-text',
      )}
    >
      {children}
      {badge != null && badge > 0 && (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--accent)] text-[var(--on-solid)] text-[0.62rem] font-bold leading-none">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

function AgenteRow({ a, onClick }: { a: Agente; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 px-4 py-3 text-left border-b border-border last:border-b-0 hover:bg-surface2/60"
    >
      <div className="relative shrink-0">
        <Avatar name={a.nome} size="lg" {...(a.cor ? { color: a.cor } : {})} />
        <span className="absolute bottom-0 right-0 size-3 rounded-full bg-[var(--ok)] ring-2 ring-bg" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-baseline justify-between gap-2">
          <span className="text-[0.95rem] font-semibold text-text truncate">{a.nome}</span>
          <span className="shrink-0 text-[0.72rem] text-text-muted">{fmtHora(a.ultima_ts)}</span>
        </div>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[0.82rem] text-text-2 truncate">
            {a.ultima_preview || 'Toque para conversar'}
          </span>
          {a.nao_lidas > 0 && (
            <span className="inline-flex items-center justify-center shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--accent)] text-[var(--on-solid)] text-[0.68rem] font-bold leading-none">
              {a.nao_lidas > 99 ? '99+' : a.nao_lidas}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
